import { faCirclePlus } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  TextField,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import Toggle from "common/Toggle";
import React, { useState } from "react";
import toast from "react-hot-toast";
import { createFaqCategory } from "services/apiService";

const FaqCategoryCreate = ({ open, handleClose, onActionComplete }) => {
  const theme = useTheme();
  const fullScreen = useMediaQuery(theme.breakpoints.down("sm"));

  const [formData, setFormData] = useState({
    name: "",
    description: "",
    is_active: 1,
  });

  const [errors, setErrors] = useState({
    name: "",
    description: "",
  });

  const handleInputChange = ({ target: { id, value } }) => {
    setFormData((prevData) => ({
      ...prevData,
      [id]: value,
    }));
    setErrors((prevErrors) => ({
      ...prevErrors,
      [id]: "",
    }));
  };

  const handleToggleChange = (checked: number, field: string) => {
    setFormData((prevData) => ({
      ...prevData,
      [field]: checked ? 1 : 0,
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const newErrors: any = {};
    if (!formData.name) newErrors.name = "Name is required";
    if (!formData.description)
      newErrors.description = "Description is required";
    if (Object.keys(newErrors).length) {
      setErrors(newErrors);
      return;
    }
    try {
      const response = await createFaqCategory(formData);
      if (response.status_code === 200) {
        handleClose();
        onActionComplete();
      }
    } catch (error) {
      toast.error("Error creating FAQ category. Please try again.");
    }
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      fullScreen={fullScreen}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 3,
          padding: 2,
        },
      }}
    >
      <DialogTitle sx={{ fontWeight: 600, textAlign: "center" }}>
        Create FAQ Category
      </DialogTitle>
      <DialogContent>
        <form onSubmit={handleSubmit}>
          <Grid container spacing={3} mt={1}>
            <Grid item xs={12}>
              <Typography variant="subtitle1" fontWeight={500}>
                Name
              </Typography>
              <TextField
                variant="outlined"
                id="name"
                fullWidth
                placeholder="Enter category name"
                value={formData.name}
                onChange={(e) => {
                  const value = e.target.value;
                  const filteredValue = value
                    .replace(/[^a-zA-Z\s]/g, "")
                    .slice(0, 30);
                  setFormData((prevData) => ({
                    ...prevData,
                    name: filteredValue,
                  }));
                }}
                error={!!errors.name}
                helperText={
                  errors.name || "Only letters and spaces, max 30 characters"
                }
                sx={{ mt: 1 }}
              />
            </Grid>

            <Grid item xs={12}>
              <Typography variant="subtitle1" fontWeight={500}>
                Description
              </Typography>
              <TextField
                variant="outlined"
                id="description"
                fullWidth
                placeholder="Enter description"
                value={formData.description}
                onChange={handleInputChange}
                error={!!errors.description}
                helperText={errors.description}
                sx={{ mt: 1 }}
              />
            </Grid>

            <Grid item xs={12}>
              <Typography variant="subtitle1" fontWeight={500}>
                Status
              </Typography>
              <Toggle
                checked={formData.is_active === 1}
                onChange={(checked) => handleToggleChange(checked, "is_active")}
              />
            </Grid>
          </Grid>
        </form>
      </DialogContent>

      <DialogActions sx={{ justifyContent: "space-between", px: 3, pb: 3 }}>
        <Button onClick={handleClose} color="secondary" variant="outlined">
          Cancel
        </Button>
        <Button
          variant="contained"
          color="primary"
          size="large"
          onClick={handleSubmit}
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 1,
            textTransform: "none",
            fontWeight: 500,
          }}
        >
          <FontAwesomeIcon icon={faCirclePlus} />
          Create FAQ Category
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default FaqCategoryCreate;
